import { Box, Button, Flex, Stack, Text } from '@mantine/core';
import { useDisclosure, useId } from '@mantine/hooks';
import { IconEdit as EditIcon } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { getGovEvaluationHIndexDownloadUrl } from '@/module/GovEvaluation/model/GovEvaluationApi';

import { Content } from '@/core/components';

import { FileCard, Id, useAppNotification } from '@feature/utils';

import { GovEvaluationHIndexModalForm } from '../form/GovEvaluationHIndexModalForm';
import { useUpdateHIndex } from '../model/GovEvaluationQuery';
import { GovEvalHIndexRequest, GovHIndex, HIndexType } from '../model/GovEvaluationTypes';

interface GovEvaluationHIndexProps {
  data: GovHIndex | undefined;
  hIndexType: HIndexType;

  canEdit: boolean;
  authorId: Id;
  evaluationId: Id;
}

export function GovEvaluationAuthorHIndex({
  evaluationId,
  data,
  hIndexType,
  canEdit,
  authorId,
}: GovEvaluationHIndexProps) {
  const { t } = useTranslation();
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false);
  const { isPending, mutate } = useUpdateHIndex(evaluationId);
  const titleId = useId();

  const onDownload = () => {
    window.open(getGovEvaluationHIndexDownloadUrl(evaluationId, authorId, hIndexType), '_blank');
  };

  const notification = useAppNotification();
  const onSubmit = (values: GovEvalHIndexRequest) => {
    mutate(values, {
      onSuccess: () => {
        notification.success();
        closeForm();
      },
      onError: notification.error,
    });
  };
  return (
    <Content
      type="outline"
      bg="white"
    >
      <Flex
        justify="space-between"
        align="center"
        role="group"
        aria-labelledby={titleId}
      >
        <Stack>
          <Text
            id={titleId}
            fw="500"
          >
            {t(`govEvaluation.${hIndexType}`)}
          </Text>
          {data == null && (
            <Text
              c="dimmed"
              fs="italic"
            >
              {t('unspecified')}
            </Text>
          )}
          {data != null && (
            <Box flex={1}>
              <Text
                fw="500"
                size="sm"
              >
                {t('govEvaluation.hIndex')}: {data.hindex}
              </Text>
              <FileCard
                file={data.proofFile}
                onDownload={onDownload}
              />
            </Box>
          )}
        </Stack>
        <Stack>
          <Button
            onClick={openForm}
            leftSection={<EditIcon size="14px" />}
            size="xs"
            disabled={!canEdit}
          >
            {t('button.edit')}
          </Button>
        </Stack>
      </Flex>
      {canEdit && formOpened && (
        <GovEvaluationHIndexModalForm
          isSubmitting={isPending}
          onSubmit={onSubmit}
          onClose={closeForm}
          evaluationId={evaluationId}
          hIndexType={hIndexType}
        />
      )}
    </Content>
  );
}
